import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import type { SubscriptionAdvertisement } from '../../@types/AdvertismentSubscription';
import { all as getSearchSubscriptions } from '../../actions/ad-subscription';
import { trackSoftConversionEvent } from '../../actions/tracking';
import type { RootState } from '../../reducers';
import { isAuthenticated as _isAuthenticated } from '../../selectors/auth';
import { getItem, getItemId } from '../../selectors/item';
import {
  getAdvertisementSubscriptionById,
  isAdvertiesementSubscriptionsSynced,
} from '../../selectors/subscriptions';
import { softConversionEvents } from '../../shared/tracking';
import { Button } from '../../styled-components/components/Button';
import { IconHeart, IconHeartFilled } from '../../styled-components/components/Icon';
import { Text } from '../../styled-components/components/Text';

interface SaveProps {
  onClick: (modalType: string) => void;
}

const Save = ({ onClick }: SaveProps) => {
  const dispatch = useDispatch();
  const item = useSelector(getItem);
  const isAuthenticated = useSelector(_isAuthenticated);
  const isSubscriptionSynced = useSelector(isAdvertiesementSubscriptionsSynced);
  const id = useSelector(getItemId);
  const subscription = useSelector<RootState, SubscriptionAdvertisement | null>((s) =>
    getAdvertisementSubscriptionById(s, id),
  );
  const actions = bindActionCreators(
    {
      get: getSearchSubscriptions,
      track: trackSoftConversionEvent,
    },
    dispatch,
  );
  const { t } = useTranslation(['common', 'item']);
  const onSaveClick = () => {
    actions.track(item, softConversionEvents.SOFT_CONVERSIONS_SAVE_INIT);

    if (!isAuthenticated) {
      onClick('SIGN_IN');
      return;
    }

    onClick('SUBSCRIBE');
  };

  useEffect(() => {
    if (!isSubscriptionSynced && isAuthenticated) actions.get();
  }, [isAuthenticated]);

  return (
    <>
      {subscription ? (
        <Button
          $shadow
          $fullWidth
          $size="small"
          title={t('Remove saved ad', { ns: 'item' })}
          onClick={() => onClick('REMOVE')}
        >
          <Text $tone="secondary" aria-hidden="true">
            <IconHeartFilled $size="20px" />
          </Text>
          {t('Saved', { ns: 'common' })}
        </Button>
      ) : (
        <Button
          $shadow
          $fullWidth
          $size="small"
          title={t('Save ad', { ns: 'item' })}
          onClick={onSaveClick}
        >
          <Text $tone="secondary" aria-hidden="true">
            <IconHeart $size="20px" />
          </Text>
          {t('save', { ns: 'common' })}
        </Button>
      )}
    </>
  );
};

export default Save;
